import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Info,
  ListOrdered,
  Video,
  ArrowUpRight,
} from "lucide-react";
import {
  findTutorial,
  allTutorials,
  accentClass,
} from "@/data/tutorials";
import { CmsPathChip } from "@/components/CmsPathChip";
import { LoomEmbed } from "@/components/LoomEmbed";
import { StepBlock } from "@/components/StepBlock";
import { BookmarkButton, CompleteToggle } from "@/components/Bookmark";
import { storage } from "@/lib/storage";

const NotFound = () => (
  <div className="px-8 py-20 text-center">
    <h2 className="font-display font-black text-4xl">Tutorial not found.</h2>
    <Link
      to="/"
      className="inline-block mt-4 underline font-semibold"
      data-testid="back-home-link"
    >
      ← Back home
    </Link>
  </div>
);

export const TutorialPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const tutorial = findTutorial(slug);

  useEffect(() => {
    if (tutorial) storage.pushRecent(tutorial.slug);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [tutorial]);

  const idx = useMemo(
    () => allTutorials.findIndex((t) => t.slug === slug),
    [slug]
  );
  const prev = idx > 0 ? allTutorials[idx - 1] : null;
  const next = idx >= 0 && idx < allTutorials.length - 1
    ? allTutorials[idx + 1]
    : null;

  if (!tutorial) return <NotFound />;

  const metaEntries = Object.entries(tutorial.meta || {});

  return (
    <article className="animate-fade-in">
      {/* HEADER */}
      <header
        className={`px-6 sm:px-10 lg:px-16 pt-10 pb-8 border-b-2 border-foreground ${
          accentClass[tutorial.chapterAccent]
        }`}
      >
        <nav
          className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest mb-5"
          aria-label="Breadcrumb"
        >
          <Link
            to="/"
            className="hover:underline"
            data-testid="breadcrumb-home"
          >
            Home
          </Link>
          <ChevronRight className="w-3 h-3" strokeWidth={3} />
          <span>{tutorial.chapterTitle}</span>
          <ChevronRight className="w-3 h-3" strokeWidth={3} />
          <span className="font-bold">{tutorial.id}</span>
        </nav>

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-xs font-bold uppercase tracking-widest">
              Tutorial {tutorial.id}
            </p>
            <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.02] mt-2">
              {tutorial.title}
            </h1>
            {tutorial.intro && (
              <p className="text-base sm:text-lg mt-4 max-w-3xl leading-relaxed text-foreground/80">
                {tutorial.intro}
              </p>
            )}
          </div>
          <BookmarkButton slug={tutorial.slug} />
        </div>

        {/* Meta chips */}
        <div className="mt-6 flex flex-wrap gap-3">
          {metaEntries.map(([label, value]) => (
            <CmsPathChip key={label} label={label} path={value} />
          ))}
        </div>
      </header>

      <div className="px-6 sm:px-10 lg:px-16 py-10 grid grid-cols-1 xl:grid-cols-[1fr_220px] gap-10">
        <div className="min-w-0 space-y-12">
          {/* Video */}
          {tutorial.loomUrl && (
            <section data-testid="tutorial-video-section">
              <div className="flex items-center gap-2 mb-3">
                <Video className="w-4 h-4" strokeWidth={2.5} />
                <h2
                  id="walkthrough"
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                >
                  Walkthrough
                </h2>
              </div>
              <LoomEmbed url={tutorial.loomUrl} title={tutorial.title} />
            </section>
          )}

          {/* Steps */}
          {tutorial.steps && tutorial.steps.length > 0 && (
            <section data-testid="tutorial-steps-section">
              <div className="flex items-center gap-2 mb-3">
                <ListOrdered className="w-4 h-4" strokeWidth={2.5} />
                <h2
                  id="steps"
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                >
                  {tutorial.stepsTitle || "Step-by-step"}
                </h2>
              </div>
              <ol className="bg-background brut-border brut-shadow divide-y-2 divide-foreground/10">
                {tutorial.steps.map((s, i) => (
                  <StepBlock key={i} index={i + 1}>
                    {s}
                  </StepBlock>
                ))}
              </ol>
            </section>
          )}

          {/* Callout */}
          {tutorial.callout && (
            <section data-testid="tutorial-callout">
              <div
                className={`brut-border brut-shadow p-5 flex items-start gap-3 ${
                  tutorial.callout.type === "warning"
                    ? "bg-brut-orange"
                    : "bg-brut-blue"
                }`}
              >
                {tutorial.callout.type === "warning" ? (
                  <AlertTriangle
                    className="w-6 h-6 flex-shrink-0 mt-0.5"
                    strokeWidth={2.5}
                  />
                ) : (
                  <Info
                    className="w-6 h-6 flex-shrink-0 mt-0.5"
                    strokeWidth={2.5}
                  />
                )}
                <p className="font-semibold text-base leading-relaxed">
                  {tutorial.callout.text}
                </p>
              </div>
            </section>
          )}

          {/* Complete toggle */}
          <section className="pt-2">
            <CompleteToggle
              slug={tutorial.slug}
              onChange={() => {
                /* re-render handled by Sidebar on next nav */
              }}
            />
          </section>

          {/* Prev/Next */}
          <nav className="grid sm:grid-cols-2 gap-4 pt-6 border-t-2 border-foreground/20">
            {prev ? (
              <Link
                to={`/tutorial/${prev.slug}`}
                data-testid="prev-tutorial-link"
                className="block brut-border brut-shadow brut-hover bg-background p-4 group"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <ChevronLeft className="w-3 h-3" strokeWidth={3} /> Previous
                </p>
                <p className="font-display font-extrabold text-base mt-1 leading-tight">
                  {prev.title}
                </p>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to={`/tutorial/${next.slug}`}
                data-testid="next-tutorial-link"
                className="block brut-border brut-shadow brut-hover bg-background p-4 group text-right"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 justify-end">
                  Next <ChevronRight className="w-3 h-3" strokeWidth={3} />
                </p>
                <p className="font-display font-extrabold text-base mt-1 leading-tight">
                  {next.title}
                </p>
              </Link>
            ) : (
              <div />
            )}
          </nav>
        </div>

        {/* TOC */}
        <aside
          className="hidden xl:block sticky top-24 h-fit"
          data-testid="tutorial-toc"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-l-2 border-foreground pl-3">
            On this page
          </p>
          <ul className="mt-3 space-y-2 border-l-2 border-foreground/15 pl-3">
            {tutorial.loomUrl && (
              <li>
                <a
                  href="#walkthrough"
                  className="text-sm font-semibold hover:text-foreground hover:bg-brut-yellow px-2 -mx-2"
                >
                  Walkthrough
                </a>
              </li>
            )}
            <li>
              <a
                href="#steps"
                className="text-sm font-semibold hover:text-foreground hover:bg-brut-yellow px-2 -mx-2"
              >
                Steps
              </a>
            </li>
            {tutorial.loomUrl && (
              <li>
                <a
                  href={tutorial.loomUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
                >
                  Open on Loom
                  <ArrowUpRight className="w-3 h-3" strokeWidth={2.5} />
                </a>
              </li>
            )}
          </ul>

          <div className="mt-6 brut-border bg-background p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Chapter
            </p>
            <p className="font-display font-bold text-sm mt-0.5">
              {tutorial.chapterTitle}
            </p>
            <button
              onClick={() => navigate(-1)}
              data-testid="toc-back-button"
              className="text-xs underline mt-3 font-semibold"
            >
              ← Back
            </button>
          </div>
        </aside>
      </div>
    </article>
  );
};
